import { IMsalContext } from "@azure/msal-react";
import axios, { AxiosResponse } from "axios";
import { getAccessToken, getS3grantAccessToken } from "config/token";
import { IRegionData, IGroup, IUser, IGrantPrivilegesPayload, IGrantPrivilegesMode, ICreateSpacePayload, IEditSpacePayload, ICreateSpaceRetryPayload, IStudyProject, IGroupMember, ITrainingDataResponse, ITrainingDataPayload, ITrainingDataByIdPayload, ITrainingDataByIdResponse, IGrantPrivilegesResponse } from "./types";
import { NEXUS_API_BASE_URL, NEXUS_API_BASE_URL_V2 } from "const";
import { $datazoneUrlHost, $nexus_api_base_url_v1 } from "http/index";
import { encryptString } from "utils/encrypt";
import { getHttpErrorMessage } from "./constants";

export const fetchRegionData = async (msalInstance: IMsalContext): Promise<IRegionData | null> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

        const requestURL = `${NEXUS_API_BASE_URL}/nexus/default?region=`;

        const config = {
            headers: { "Authorization": bearerToken },
        };

        const response = await axios.get(requestURL, config);

        if (response.data) {
            return {
                projects: response.data.projects,
                studies: response.data.studies,
            };
        }

        return null;
    } catch (error) {
        console.error("Error fetching region data:", error);
        return null;
    }
};

export const fetchGroups = async (
    msalInstance: IMsalContext,
    search: string,
): Promise<IGroup[]> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

        // Use axios directly (or replace with $datazoneUrlHost if you're standardizing on that)
        const response: AxiosResponse<{ data: { id: string, name: string }[] }> = await $nexus_api_base_url_v1.get(`/azure-ad/groups?q=${encodeURIComponent(search.replace(/"/g, "").trim())}&limit=20`, {
            headers: {
                Authorization: bearerToken,
            },
        });

        if (Array.isArray(response.data.data)) {
            return response.data.data.map((item) => ({
                id: item.id,
                name: item.name,
            }));
        }

        return [];
    } catch (error) {
        console.error("Error fetching groups:", error);
        return [];
    }
};

export const fetchUsers = async (
    msalInstance: IMsalContext,
    search: string,
): Promise<IUser[]> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

        const response: AxiosResponse<{ data: { id: string, mail: string, name: string }[] }> = await $nexus_api_base_url_v1.get(`/azure-ad/users?q=${encodeURIComponent(search.replace(/"/g, "").trim())}&limit=20`, {
            headers: {
                Authorization: bearerToken,
            },
        });

        if (Array.isArray(response.data.data)) {
            return response.data.data.map((item) => ({
                id: item.id,
                mail: item.mail,
                name: item.name,
            }));
        }

        return [];
    } catch (error) {
        console.error("Error fetching users:", error);
        return [];
    }
};

const GRANT_PRIVILEGES_ENDPOINTS: Record<IGrantPrivilegesMode, string> = {
    "create": "create-space",
    "edit": "update-space",
    "create-retry": "create-space-retry",
};

export async function grantPrivileges(
    msalInstance: IMsalContext,
    payload: ICreateSpacePayload,
    mode: "create",
): Promise<IGrantPrivilegesResponse | false>;
export async function grantPrivileges(
    msalInstance: IMsalContext,
    payload: IEditSpacePayload,
    mode: "edit",
): Promise<IGrantPrivilegesResponse | false>;
export async function grantPrivileges(
    msalInstance: IMsalContext,
    payload: ICreateSpaceRetryPayload,
    mode: "create-retry",
): Promise<IGrantPrivilegesResponse | false>;
export async function grantPrivileges(
    msalInstance: IMsalContext,
    payload: IGrantPrivilegesPayload,
    mode: IGrantPrivilegesMode,
): Promise<IGrantPrivilegesResponse | false> {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

        const endpoint = GRANT_PRIVILEGES_ENDPOINTS[mode];

        const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/${endpoint}`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        const bodyParameters = {
            ...payload,
            at: token?.at,
            //retry,
        };

        const response: AxiosResponse<IGrantPrivilegesResponse> = await axios.post(requestURL, bodyParameters, config);

        return response.data; 
    } catch (err) {
        console.error("Error Creating/Updating space", err);

        if (axios.isAxiosError(err)) {
            // No response means the request never reached the server (network/CORS/timeout)
            if (!err.response) {
                throw new Error("Unable to connect to the server. Please check your network connection and try again.");
            }

            const status = err.response.status;
            const responseData = err.response.data;

            if (status === 403 && responseData?.app_exception === "SpaceTypeCannotBeChanged") {
                return false;
            }

            // Re-throw for EditSpaceForbiddenForNonOwners — caught by the retry handler in grid
            if (status === 403 && responseData?.app_exception === "EditSpaceForbiddenForNonOwners") {
                throw err;
            }

            // Re-throw 400 with its original shape so callers can parse field-level validation errors
            if (status === 400) {
                throw err;
            }

            const apiMessage: string | undefined =
                responseData?.context?.message || responseData?.message || responseData?.detail;

            throw new Error(getHttpErrorMessage(status, apiMessage));
        }

        // Non-Axios errors (e.g. programming errors, unexpected throws)
        throw new Error("An unexpected error occurred. Please try again.");
    }
}

export const validateSpaceName = async (msalInstance: IMsalContext, spaceName: string): Promise<boolean> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/check-availability?space_name=${spaceName}`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        const response = await axios.get(requestURL, config);
        const { data }: { data: boolean } = response;

        return data; // Assuming API returns { exists: true/false }
    } catch (error) {
        console.error("Error checking space name:", error);
        return true; // Default to "not existing" if API fails
    }
};

export const getProjectsAndStudies = async (
    msalInstance: IMsalContext,
    studyToMatch: string,
): Promise<{ matched: boolean; projectDisplayName?: string }> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token?.token ?? "");

        const requestURL = `${NEXUS_API_BASE_URL}/mdm/get-Studies-and-Projects`;

        const response = await axios.get<IStudyProject[]>(requestURL, {
            headers: { Authorization: bearerToken },
        });

        const data = response.data;

        const matchedItem = data.find(
            (item) =>
                `${item.study_name} (${item.study_id})`.trim() === studyToMatch.trim(),
        );

        if (matchedItem) {
            const projectDisplayName = `${matchedItem.project_name} (${matchedItem.project_id})`;
            return { matched: true, projectDisplayName };
        } else {
            return { matched: false };
        }
    } catch (error) {
        console.error("Error fetching studies/projects", error);
        return { matched: false };
    }
};

export const validate_git_link = async (
    msalInstance: IMsalContext,
    git_link: string,
): Promise<boolean> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/git-service/search_git_link?git_link=${git_link}`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        const response = await axios.get(requestURL, config);
        const { data }: { data: boolean } = response;

        return data; 
    } catch (error) {
        console.error("Error checking space name:", error);
        return false;
    }
};

export const get_git_list = async (
    msalInstance: IMsalContext,
): Promise<string[]> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/git-service/get_git_list`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        const response = await axios.get(requestURL, config);
        const { data }: { data: string[] } = response;

        return data; 
    } catch (error) {
        console.error("Error checking space name:", error);
        return [];
    }
};
// Fetch group members via Nexus Backend API (V2 - Supports multiple groups)
export const fetchGroupMembers = async (
    msalInstance: IMsalContext,
    groupIds: string | string[],
    searchTerm?: string,
): Promise<IGroupMember[]> => {
    try {
        // Get both tokens - backend auth token and Graph API access token
        const backendToken = await getAccessToken(msalInstance);
        const graphToken = await getS3grantAccessToken(msalInstance, true);
        
        const bearerToken = "Bearer " + (backendToken?.token ?? "");
        const accessToken = graphToken?.at;
        
        if (!accessToken) throw new Error("No Graph API access token available");

        const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/group-members`;

        // Support both single group (backward compatible) and multiple groups
        const isArray = Array.isArray(groupIds);
        const requestBody: any = {
            at: accessToken,
            search_term: searchTerm || null,
        };

        // Use group_ids for array, group_id for single (backward compatible)
        if (isArray) {
            requestBody.group_ids = groupIds;
        } else {
            requestBody.group_id = groupIds;
        }

        const response = await axios.post(
            requestURL,
            requestBody,
            {
                headers: {
                    Authorization: bearerToken,
                    "Content-Type": "application/json",
                },
            },
        );

        // Backend handles pagination and returns all unique members (deduplicated for multiple groups)
        if (Array.isArray(response.data)) {
            return response.data.map((member: any) => ({
                id: member.id,
                displayName: member.displayName,
                mail: member.mail,
                userPrincipalName: member.userPrincipalName,
            }));
        }

        return [];
    } catch (error) {
        console.error("Error fetching group members from backend:", error);
        return [];
    }
};


export const fetchTrainingData = async (
    msalInstance: IMsalContext,
    payload: ITrainingDataPayload,
): Promise<ITrainingDataResponse | null> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

        const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/training-data`;

        const config = {
            headers: { 
                Authorization: bearerToken,
                "Content-Type": "application/json",
            },
        };

        // Validate payload
        if (!payload.group_name) {
            throw new Error("'group_name' must be provided");
        }

        if (typeof payload.group_name !== "string") {
            throw new Error("'group_name' must be a string");
        }

        const requestBody = {
            group_name: payload.group_name,
            limit: payload.limit || 1000,
        };

        const response = await axios.post(requestURL, requestBody, config);

        if (response.data) {
            return {
                data: response.data.data || [],
                count: response.data.count || 0,
                query_time_seconds: response.data.query_time_seconds || 0,
                filters_applied: response.data.filters_applied || {
                    group_name: payload.group_name,
                    limit: payload.limit || 1000,
                },
            };
        }

        return null;
    } catch (error) {
        console.error("Error fetching training data:", error);
        throw error;
    }
};

export const fetchParametersSecrets = async (
    msalInstance: IMsalContext,
    spaceName: string,
    domainName: string,
): Promise<{ parameters: Array<{ name: string; value: string; path?: string }>, secrets: Array<{ name: string; value: string; arn?: string }> } | null> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token?.token ?? "");
        // Using BYOC Parameter Management API endpoint - GET /spaces/{space_name}/parameters
        const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/space-parameters/${spaceName}`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        const response = await axios.get(requestURL, config);
                
        const parameters: Array<{ name: string; value: string; path?: string }> = [];
        const secrets: Array<{ name: string; value: string; arn?: string }> = [];
        
        // Handle direct response structure
        if (response.data?.parameters && Array.isArray(response.data.parameters)) {
            response.data.parameters.forEach((param: { name: string; value: string; path?: string }) => {
                parameters.push({
                    name: param.name,
                    value: param.value, // Parameter values are not encrypted
                    path: param.path,
                });
            });
        }
        
        // Handle secrets - values are encrypted and need decryption
        if (response.data?.secrets && Array.isArray(response.data.secrets)) {
            response.data.secrets.forEach((secret: { name: string; value: string; arn?: string }) => {
                secrets.push({
                    name: secret.name,
                    value: secret.value, // Secret values are encrypted - will be decrypted in the component
                    arn: secret.arn,
                });
            });
        }
        
        return { parameters, secrets };
        
    } catch (error) {
        console.error("Error fetching parameters/secrets:", error);
        return null;
    }
};

export interface IUpdateSpaceParametersResponse {
    space_name: string;
    message: string;
    updated_parameters: number;
    updated_secrets: number;
    deleted_parameters: number;
    deleted_secrets: number;
}

export const updateSpaceParameters = async (
    msalInstance: IMsalContext,
    spaceName: string,
    parameters: Array<{ name: string; value: string }>,
    secrets: Array<{ name: string; value: string }>,
    deletedParameters: string[],
    deletedSecrets: string[],
    edittedParams?: Array<{ field: string; name: string }>,
): Promise<IUpdateSpaceParametersResponse> => {
    const token = await getAccessToken(msalInstance);
    const bearerToken = "Bearer " + (token?.token ?? "");
    const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/space-parameters/${spaceName}`;

    const config = {
        headers: { Authorization: bearerToken, "Content-Type": "application/json" },
    };

    const encryptedParameters = parameters.map(p => ({ name: p.name, value: encryptString(p.value) }));
    const encryptedSecrets = secrets.map(s => ({ name: s.name, value: encryptString(s.value) }));

    const body: Record<string, unknown> = {
        parameters: encryptedParameters,
        secrets: encryptedSecrets,
        deleted_parameters: deletedParameters,
        deleted_secrets: deletedSecrets,
    };

    if (edittedParams && edittedParams.length > 0) {
        body.editted_params = edittedParams;
    }

    const response = await axios.post(requestURL, body, config);
    return response.data as IUpdateSpaceParametersResponse;
};


export const fetchTrainingDataForGroups = async (
    msalInstance: IMsalContext,
    groupNames: string[],
    limit = 1000,
): Promise<{
    successful: number;
    failed: number;
    results: Array<{ group: string; data: ITrainingDataResponse | null; error?: any }>;
}> => {
    if (!groupNames || groupNames.length === 0) {
        return { successful: 0, failed: 0, results: [] };
    }

    // Fetch training data for each group individually
    const results = await Promise.allSettled(
        groupNames.map(async (groupName) => {
            try {
                const trainingData = await fetchTrainingData(msalInstance, {
                    group_name: groupName,
                    limit,
                });
                
                return { group: groupName, data: trainingData };
            } catch (error) {
                console.error(`Failed to fetch training data for "${groupName}":`, error);
                throw error;
            }
        }),
    );

    // Process results
    const processedResults = results.map((result, index) => {
        if (result.status === "fulfilled") {
            return result.value;
        } else {
            return {
                group: groupNames[index],
                data: null,
                error: result.reason,
            };
        }
    });

    const successful = results.filter(r => r.status === "fulfilled").length;
    const failed = results.filter(r => r.status === "rejected").length;

    return {
        successful,
        failed,
        results: processedResults,
    };
};


export const fetchTrainingDataByIds = async (
    msalInstance: IMsalContext,
    trainingIds: string[],
    limit = 1000,
): Promise<ITrainingDataByIdResponse | null> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

        const requestURL = `${NEXUS_API_BASE_URL}/nexus/training-data-by-ids`;

        const config = {
            headers: { 
                Authorization: bearerToken,
                "Content-Type": "application/json",
            },
        };

        const requestBody = {
            training_ids: trainingIds,
            limit: limit,
        };

        const response = await axios.post(requestURL, requestBody, config);

        if (response.data) {
            return {
                data: response.data.data || [],
                count: response.data.count || 0,
                query_time_seconds: response.data.query_time_seconds || 0,
            };
        }

        return null;
    } catch (error) {
        console.error("Error fetching training data by IDs:", error);
        return null;
    }
};
